import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RotateCcw, Trash2, Camera, User, Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "Pending", label: "Pending" },
  { key: "Approved", label: "Approved" },
  { key: "Rejected", label: "Rejected" },
];

function formatApiErrorDetail(d) {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join("; ");
  return "";
}

export default function Memories() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin" || user?.role === "editor";
  const [tab, setTab] = useState("Pending");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/memories", { params: { status: tab } });
      setList(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);

  const setStatus = async (m, status) => {
    setBusyId(m.id);
    try {
      await api.patch(`/admin/memories/${m.id}/status`, { status });
      toast.success(status === "Approved" ? "Approved & published" : status === "Rejected" ? "Rejected" : "Marked Pending");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    } finally { setBusyId(""); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/memories/${confirmDelete.id}`);
      toast.success("Submission deleted");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const counts = useMemo(() => ({ [tab]: list.length }), [tab, list]);

  return (
    <div className="space-y-5" data-testid="adm-memories">
      <div className="bg-white rounded-sm border border-[#e5e0d8] p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Trust CMS</div>
            <h1 className="font-serif text-2xl text-[#1c1a17] flex items-center gap-2"><Camera size={18} /> Share Your Memories</h1>
            <div className="text-sm text-[#6b655b] mt-0.5">Review submissions from alumni and the GS Mandal family. Approved memories appear on the public site.</div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 border-b border-[#e5e0d8]">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`adm-memories-tab-${t.key.toLowerCase()}`}
              className={`text-[11px] uppercase tracking-wider font-bold px-4 py-2.5 transition-colors ${
                tab === t.key ? "bg-[#234333] text-white" : "text-[#6b655b] hover:bg-[#f5f1e8]"
              }`}
            >
              {t.label}{tab === t.key && ` (${counts[tab] || 0})`}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="text-sm text-[#6b655b] flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
          ) : list.length === 0 ? (
            <div className="bg-[#f5f1e8] rounded-sm p-8 text-center text-sm text-[#6b655b]">No {tab.toLowerCase()} submissions.</div>
          ) : (
            <div className="space-y-4">
              {list.map((m) => (
                <div key={m.id} className="border border-[#e5e0d8] rounded-sm bg-[#fbf8f1] p-4" data-testid={`adm-memory-${m.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#a5875a] font-semibold">
                        {m.approx_year || "Year unknown"} · Submitted {new Date(m.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </div>
                      <div className="text-sm font-semibold text-[#1c1a17] mt-0.5 flex items-center gap-2"><User size={13} />{m.is_anonymous ? "Anonymous submitter" : (m.submitter_name || "—")}</div>
                      {!m.is_anonymous && (m.submitter_email || m.submitter_phone) && (
                        <div className="text-[11px] text-[#6b655b] mt-0.5">{m.submitter_email}{m.submitter_phone ? ` · ${m.submitter_phone}` : ""}</div>
                      )}
                      {m.institute_name && (
                        <div className="text-[11px] text-[#234333] font-semibold mt-1">Institute: {m.institute_name}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {canEdit && tab !== "Approved" && (
                        <button type="button" onClick={() => setStatus(m, "Approved")} disabled={busyId === m.id} data-testid={`adm-memory-approve-${m.id}`} className="inline-flex items-center gap-1 bg-green-700 hover:bg-green-800 text-white text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-sm">
                          <CheckCircle2 size={12} /> Approve
                        </button>
                      )}
                      {canEdit && tab !== "Rejected" && (
                        <button type="button" onClick={() => setStatus(m, "Rejected")} disabled={busyId === m.id} data-testid={`adm-memory-reject-${m.id}`} className="inline-flex items-center gap-1 bg-red-700 hover:bg-red-800 text-white text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-sm">
                          <XCircle size={12} /> Reject
                        </button>
                      )}
                      {canEdit && tab !== "Pending" && (
                        <button type="button" onClick={() => setStatus(m, "Pending")} disabled={busyId === m.id} data-testid={`adm-memory-reopen-${m.id}`} className="inline-flex items-center gap-1 bg-[#c78e42] hover:bg-[#a5875a] text-white text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-sm">
                          <RotateCcw size={12} /> Reopen
                        </button>
                      )}
                      {canEdit && (
                        <button type="button" onClick={() => setConfirmDelete(m)} data-testid={`adm-memory-delete-${m.id}`} className="inline-flex items-center gap-1 border border-red-300 text-red-700 hover:bg-red-50 text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-sm">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {(m.photos || []).map((p, i) => (
                      <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="block bg-white border border-[#e5e0d8] rounded-sm overflow-hidden">
                        <img src={p.url} alt={p.caption || ""} className="w-full aspect-[4/3] object-cover" loading="lazy" />
                        {p.caption && <div className="p-1.5 text-[10px] text-[#6b655b] line-clamp-2">{p.caption}</div>}
                      </a>
                    ))}
                  </div>

                  <div className="mt-3 text-[11px] text-[#6b655b] space-y-0.5">
                    {m.people_in_photo && <div><b className="text-[#1c1a17]">People:</b> {m.people_in_photo}</div>}
                    {m.photographer_name && <div><b className="text-[#1c1a17]">Photographer:</b> {m.photographer_name}</div>}
                    {m.story && (
                      <div className="mt-2 bg-white border border-[#e5e0d8] rounded-sm p-3 text-[12px] text-[#1c1a17] leading-relaxed whitespace-pre-wrap">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-[#a5875a] font-semibold mb-1">Story</div>
                        {m.story}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-xl">Delete submission?</AlertDialogTitle>
            <AlertDialogDescription>All photographs (including from Cloudinary) will be permanently removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="adm-memory-confirm-delete" className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
